import classNames from 'classnames';
import { type VoidFunctionComponent } from 'react';

import { Badge } from '../../generic/Badge';
import { Icon, icons } from '../../generic/Icon';
import { useReportFlow } from '../hooks/useReportFlow';
import type { IconClassName } from '../../generic/Icon';

import styles from './ReportFlowSelector.module.less';

type ReportFlowSelectorOptionProps = {
  /**
   * The icon to display in the top left of the card.
   */
  icon: IconClassName;
  /**
   * The title/heading for this option.
   */
  title: string;
  /**
   * The description text for this option.
   */
  description: string;
  /**
   * Whether this option is currently selected.
   */
  isSelected?: boolean;
  /**
   * Optional badge text to display in the top right corner (e.g. "Recommended").
   */
  badge?: string;
  /**
   * Click handler for when this option is selected.
   */
  onClick: () => void;
};

/**
 * A panel-style selector component for choosing between report flow options.
 * Can optionally display a badge (e.g. "Recommended") in the top right corner.
 */
const ReportFlowSelectorOption: VoidFunctionComponent<ReportFlowSelectorOptionProps> = ({
  icon,
  title,
  description,
  isSelected = false,
  badge,
  onClick,
}) => {
  return (
    <button
      type="button"
      className={classNames(styles.card, {
        [styles.selected]: isSelected,
      })}
      onClick={onClick}
    >
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <Icon icon={icon} />
        </div>
        {badge && <Badge color="lighter-gray">{badge}</Badge>}
      </div>
      <div className={styles.content}>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.description}>{description}</p>
      </div>
    </button>
  );
};

/**
 * The "selector" which actually renders both of the options for us.
 * Note that this is a "smart" component, in that it
 */
export const ReportFlowSelector: VoidFunctionComponent = () => {
  const [flow, setFlow] = useReportFlow();

  return (
    <div className={styles.container}>
      <ReportFlowSelectorOption
        icon={icons.fileO}
        title="Standard"
        description="Get started instantly with FOSSA's pre-set filters — ideal for quick, consistent reports."
        isSelected={flow === 'standard'}
        badge="Recommended"
        onClick={() => setFlow('standard')}
      />
      <ReportFlowSelectorOption
        icon={icons.sliders}
        title="Custom"
        description="Full flexibility to define every filter, view, and data point — built for power users."
        isSelected={flow === 'custom'}
        onClick={() => setFlow('custom')}
      />
    </div>
  );
};
